import { useMemo } from 'react';
import { assetConstants, type ChainflipAsset } from '@chainflip/utils/chainflip';
import { isNotNullish } from '@chainflip/utils/guard';
import BigNumber from 'bignumber.js';
import { Link } from '@/shared/components/atoms/Link';
import Pill from '@/shared/components/flip-ui-kit/Pill';
import BoostPoolLogo from '@/shared/components/logo/BoostPoolLogo';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import AgeWithTooltip from '@/shared/components/molecules/AgeWithTooltip';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import { BOOST_FEE_FLIP_BURN_PERCENT } from '@/shared/constants';
import {
  type GetPoolBoostsQuery,
  type GetBoostSharesByAccountAndPoolQuery,
} from '@/shared/graphql/generated/lp/graphql';
import {
  getBoostSharesByAccountAndPoolQuery,
  getPoolBoostSharesQuery,
} from '@/shared/graphql/lp/boost';
import useBoostPoolId from '@/shared/hooks/useBoostPoolId';
import useRemotePagination from '@/shared/hooks/useRemotePagination';
import useTableSort from '@/shared/hooks/useTableSort';
import { chainflipAssetMap, FLIP_SYMBOL } from '@/shared/utils/env';
import { pipsToPercentString, formatUsdValue } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';

type BoostShare = (
  | NonNullable<GetPoolBoostsQuery['boostShares']>
  | NonNullable<GetBoostSharesByAccountAndPoolQuery['account']>['shares']
)['edges'][number]['node'];

const TokenAmountCol = ({
  assetAmount,
  asset,
  ticker,
  usdValue,
  lost,
  isFee = false,
  canBeFractional = true,
}: {
  assetAmount: string;
  asset: ChainflipAsset;
  ticker: string;
  usdValue?: string;
  lost?: boolean;
  isFee?: boolean;
  canBeFractional?: boolean;
}) => {
  let tokenAmount = TokenAmount.fromAsset(assetAmount, asset);

  const tokenAmountSign = lost ? '-' : '+';
  const usdValueSign = lost ? '-' : undefined;

  let amountColor = 'text-cf-light-3';

  if (lost !== undefined) {
    if (tokenAmountSign === '-') {
      amountColor = 'text-cf-red-2';
    } else {
      amountColor = 'text-cf-green-3';
    }
  }

  // the bookkeeping in the protocol tracks up to one one-hundredth of a Satoshi
  // so if we get an amount of 0, it's actually a fractional amount
  const isFractional = canBeFractional && tokenAmount.eq(0);
  if (isFractional) tokenAmount = TokenAmount.fromAsset(1, asset);

  return (
    <div className="flex flex-col gap-y-px text-14">
      <div className="flex items-center gap-x-2">
        <TokenWithChainByAsset asset={asset} />
        <span className={`flex items-center gap-x-2 ${amountColor}`}>
          {isFee && tokenAmountSign}
          {isFractional && '<'}
          {tokenAmount.toFormatted()} {ticker}
        </span>
      </div>
      <span className="text-cf-light-2">
        {usdValueSign}
        {formatUsdValue(usdValue)}
      </span>
    </div>
  );
};

const Row = ({
  boostShare,
  includePool,
  blockExplorerUrl,
}: {
  boostShare: BoostShare;
  includePool: boolean;
  blockExplorerUrl?: string;
}) => {
  const { channel, swapRequestId } = boostShare.boost;
  const channelId = `${channel.issuedBlockId}-${channel.chain}-${channel.channelId}`;
  // TODO(boost): hard coded values used here, should be updated if we introduce new boost pools
  const asset = 'Btc';
  const feeTierPips = 5;
  const ticker = chainflipAssetMap[asset].symbol;
  const channelPath =
    channel.type === 'LIQUIDITY' ? `/channels/${channelId}` : `/channels/${channelId}`;
  const channelUrl = blockExplorerUrl
    ? new URL(channelPath, blockExplorerUrl).toString()
    : channelPath;
  const swapPath = `/swaps/${swapRequestId}`;
  const swapUrl = blockExplorerUrl ? new URL(swapPath, blockExplorerUrl).toString() : swapPath;

  const earnedFeeAmount = boostShare.lost ? boostShare.amount : boostShare.fee;
  const earnedFeeUsdValue = boostShare.lost ? boostShare.amountUsd : boostShare.feeUsd;
  const amountRatio = new BigNumber(boostShare.amount).dividedBy(boostShare.boost.depositAmount);
  const networkFeePortion = amountRatio
    .times(boostShare.boost.networkFeePortion ?? 0)
    .toFixed(assetConstants[asset].decimals);
  const networkFeePortionUsd = boostShare.boost.networkFeePortionUsd
    ? amountRatio.times(boostShare.boost.networkFeePortionUsd).toFixed()
    : undefined;

  return (
    <tr className="text-cf-light-3">
      <td className="whitespace-nowrap">
        {channel.type === 'BROKER' ? (
          <span>Vault swap</span>
        ) : (
          <Link
            href={channelUrl}
            noPropagate
            underline
            target={blockExplorerUrl ? '_blank' : undefined}
            className="text-14"
          >
            {channelId}
          </Link>
        )}
      </td>
      <td className="whitespace-nowrap">
        {swapRequestId ? (
          <div className="flex flex-row items-center space-x-2">
            <Link
              href={swapUrl}
              underline
              noPropagate
              target={blockExplorerUrl ? '_blank' : undefined}
            >
              #{swapRequestId}
            </Link>
            <CopyButton textToCopy={swapRequestId} />
          </div>
        ) : (
          'N/A'
        )}
      </td>
      {includePool && (
        <td className="whitespace-nowrap">
          <div className="flex items-center gap-x-2 ">
            <BoostPoolLogo />
            <Pill text={pipsToPercentString(feeTierPips)} color="neutral" />
          </div>
        </td>
      )}
      <td className="whitespace-nowrap">
        <TokenAmountCol
          assetAmount={boostShare.amount}
          usdValue={boostShare.amountUsd ?? undefined}
          asset={asset}
          ticker={ticker}
        />
      </td>
      <td className="whitespace-nowrap">
        <TokenAmountCol
          assetAmount={earnedFeeAmount}
          usdValue={earnedFeeUsdValue ?? undefined}
          asset={asset}
          ticker={ticker}
          lost={boostShare.lost}
          isFee
        />
      </td>
      <td className="whitespace-nowrap">
        {boostShare.boost.settledAtTimestamp && (
          <TokenAmountCol
            assetAmount={networkFeePortion}
            usdValue={networkFeePortionUsd}
            asset={asset}
            ticker={ticker}
            canBeFractional={boostShare.boost.networkFeeSwapRequestId != null}
          />
        )}
      </td>
      <td className="whitespace-nowrap text-right text-12 text-cf-light-2">
        <AgeWithTooltip timestamp={boostShare.boost.executedAtTimestamp} />
      </td>
    </tr>
  );
};

export const BoostedDepositsTable = ({
  asset,
  feeBps,
  accountId,
  blockExplorerUrl,
}: {
  asset?: ChainflipAsset;
  feeBps?: number;
  accountId?: string;
  blockExplorerUrl?: string;
}) => {
  const allPools = asset === undefined && feeBps === undefined;
  const isAccountView = accountId !== undefined;

  const { sort, setCol } = useTableSort({});
  const sortableColumns = useMemo(() => {
    const [amountCol, feeCol, networkFeeCol, createdCol] = allPools ? [3, 4, 5, 6] : [2, 3, 4, 5];

    return {
      [amountCol]: {
        ASC: 'AMOUNT_USD_ASC',
        DESC: 'AMOUNT_USD_DESC',
      },
      [feeCol]: {
        ASC: 'FEE_USD_ASC',
        DESC: 'FEE_USD_DESC',
      },
      [networkFeeCol]: {
        ASC: 'AMOUNT_USD_ASC',
        DESC: 'AMOUNT_USD_DESC',
      },
      [createdCol]: {
        ASC: 'BOOST_ID_DESC',
        DESC: 'BOOST_ID_DESC',
      },
    } as const;
  }, [allPools]);

  const boostShareOrderBy = useMemo(
    () => sortableColumns[sort.col]?.[sort.direction] ?? 'BOOST_ID_DESC',
    [sort],
  );

  const {
    data: boostPoolBoostShares,
    paginationControls: poolPaginationControls,
    isLoading: poolSharesLoading,
  } = useRemotePagination(getPoolBoostSharesQuery, {
    variables: {
      asset: asset as ChainflipAsset,
      feeTierPips: feeBps as number,
      orderBy: boostShareOrderBy,
    },
    enabled: !isAccountView,
    context: { clientName: 'lpProcessor' },
    refetchInterval: 30_000,
    totalCount: (data) => data.boostShares?.totalCount || 0,
    pageInfo: (data) => data.boostShares?.pageInfo,
  });

  const { boostPoolId } = useBoostPoolId({ asset, feeBps });

  const {
    data: accountBoostShares,
    paginationControls: accountPaginationControls,
    isLoading: accountPoolSharesLoading,
  } = useRemotePagination(getBoostSharesByAccountAndPoolQuery, {
    variables: { accountId: accountId as string, boostPoolId, orderBy: boostShareOrderBy },
    enabled: isAccountView,
    refetchInterval: 30_000,
    context: { clientName: 'lpProcessor' },
    totalCount: (data) => data.account?.shares.totalCount || 0,
    pageInfo: (data) => data.account?.shares.pageInfo,
  });

  const shares = (
    !isAccountView ? boostPoolBoostShares?.boostShares : accountBoostShares?.account?.shares
  )?.edges;

  return (
    <TableV2
      isLoading={poolSharesLoading || accountPoolSharesLoading}
      columns={[
        { name: 'Deposit channel', sortable: false },
        { name: 'Swap', sortable: false },
        ...(allPools
          ? [
              { name: 'Boost Pool', sortable: false },
              { name: 'Amount', sortable: true },
            ]
          : [{ name: 'Amount', sortable: true }]),
        { name: 'Earned Fee', sortable: true },
        {
          name: 'Sent to burn pool',
          tooltip: (
            <div>
              For every fee collected, a contribution of {BOOST_FEE_FLIP_BURN_PERCENT}% will be sent
              to the ${FLIP_SYMBOL} buy-and-burn mechanism.{' '}
              <Link
                className="underline"
                target="_blank"
                href="https://docs.chainflip.io/protocol/token-economics/incentive-design-emission-and-burning#the-network-fee"
                rel="noreferrer"
              >
                Learn more
              </Link>
            </div>
          ),
          sortable: true,
        },
        { alignment: 'right', name: 'Created', sortable: true },
      ]}
      rows={
        shares
          ?.map(({ node }) => (
            <Row
              key={node.id}
              boostShare={node}
              includePool={allPools}
              blockExplorerUrl={blockExplorerUrl}
            />
          ))
          // TOOD(assethub): remove
          .filter(isNotNullish) || []
      }
      paginationControls={!isAccountView ? poolPaginationControls : accountPaginationControls}
      sorting={{ setCol, sort }}
    />
  );
};
